import { useEffect, useState } from "react";
import axios from "axios";
import { X, Clock, User as UserIcon, ShieldCheck } from "lucide-react";
import { API } from "../App";

const EVENT_LABELS = {
  status_change: "Status changed",
  driver_assigned: "Driver assigned",
  driver_changed: "Driver changed",
  proposal_sent: "Proposal sent",
  proposal_accepted: "Proposal accepted",
  cancelled: "Cancelled",
  paid_toggled: "Paid flag changed",
  manual_created: "Manually created",
};

function formatVal(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "yes" : "no";
  return String(v);
}

export default function StatusHistoryModal({ quoteId, onClose }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/quotes/${quoteId}/history`)
      .then((res) => { if (alive) setEvents(res.data.history || []); })
      .catch((e) => { console.error("Failed to load history", e); if (alive) setEvents([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [quoteId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="status-history-modal">
      <div className="bg-[#121212] border border-white/10 rounded-sm w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#C5A059]" /> Status history
            </h3>
            <p className="text-white/40 text-xs font-mono mt-1">{quoteId}</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="history-close-btn"><X className="w-5 h-5" /></button>
        </div>

        <div className="overflow-y-auto p-5 space-y-3">
          {loading ? (
            <div className="flex justify-center py-12"><div className="spinner" /></div>
          ) : events.length === 0 ? (
            <p className="text-white/40 text-sm italic text-center py-12">No events recorded for this booking.</p>
          ) : (
            [...events].reverse().map((ev) => (
              <div key={ev.event_id} className="bg-[#0F0F0F] border border-white/5 rounded-sm p-3" data-testid={`history-event-${ev.event_id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[#C5A059] text-xs uppercase tracking-wide">{EVENT_LABELS[ev.event_type] || ev.event_type}</span>
                  <span className="text-white/40 text-[10px] font-mono">{ev.at?.replace("T", " ").slice(0, 19)}</span>
                </div>
                <div className="text-white/70 text-sm">
                  <span className="text-white/40">{formatVal(ev.old_value)}</span>
                  <span className="mx-2 text-white/30">→</span>
                  <span className="text-white">{formatVal(ev.new_value)}</span>
                </div>
                <div className="flex items-center gap-2 mt-2 text-white/50 text-xs">
                  {ev.actor_type === "admin" || ev.actor_type === "system" ? (
                    <ShieldCheck className="w-3 h-3 text-violet-400" />
                  ) : (
                    <UserIcon className="w-3 h-3 text-emerald-400" />
                  )}
                  <span>{ev.actor_name} <span className="text-white/30">({ev.actor_type})</span></span>
                </div>
                {ev.details && (
                  <p className="text-white/50 text-xs italic mt-2 border-t border-white/5 pt-2">{ev.details}</p>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
